import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";

function pinIcon(tone) {
  const colors = {
    green: "#475C4D",
    yellow: "#D4A347",
    grey: "#78716C",
  };
  const c = colors[tone] || colors.grey;
  const html = `
    <div style="position:relative;width:28px;height:36px;">
      <svg viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.25))">
        <path d="M14 0 C21.73 0 28 6.27 28 14 C28 22 14 36 14 36 C14 36 0 22 0 14 C0 6.27 6.27 0 14 0 Z" fill="${c}"/>
        <circle cx="14" cy="14" r="5" fill="white"/>
      </svg>
    </div>
  `;
  return L.divIcon({
    html,
    className: "kridam-pin",
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -32],
  });
}

function Recenter({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13, { animate: true });
    } else {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds.pad(0.2), { animate: true });
    }
  }, [points, map]);
  return null;
}

export default function LiveMap({ salesmen }) {
  const points = useMemo(
    () =>
      salesmen
        .filter((s) => s.last_location?.lat != null && s.last_location?.lng != null)
        .map((s) => {
          const mins =
            (Date.now() - new Date(s.last_location.at).getTime()) / 60000;
          const tone = mins < 5 ? "green" : mins < 30 ? "yellow" : "grey";
          return {
            id: s.id,
            name: s.name,
            lat: s.last_location.lat,
            lng: s.last_location.lng,
            accuracy: s.last_location.accuracy,
            at: s.last_location.at,
            tone,
            active: s.active !== false,
          };
        }),
    [salesmen],
  );

  const [key, setKey] = useState(0);
  // Rerender tones every 15s
  useEffect(() => {
    const iv = setInterval(() => setKey((k) => k + 1), 15 * 1000);
    return () => clearInterval(iv);
  }, []);

  const center = points.length > 0 ? [points[0].lat, points[0].lng] : [20.5937, 78.9629]; // fallback: India

  return (
    <div
      className="rounded-xl overflow-hidden border border-[color:var(--border)] ink-shadow"
      style={{ height: 440 }}
      data-testid="live-map-container"
    >
      <MapContainer
        center={center}
        zoom={points.length ? 13 : 5}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Recenter points={points} />
        {points.map((p) => (
          <Marker key={`${p.id}-${key}`} position={[p.lat, p.lng]} icon={pinIcon(p.tone)}>
            <Popup>
              <div style={{ fontFamily: "Work Sans, sans-serif" }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "#78716C", marginTop: 2 }}>
                  {p.tone === "green"
                    ? "Live"
                    : p.tone === "yellow"
                      ? "Recent"
                      : "Stale"}
                </div>
                <div style={{ fontSize: 11, marginTop: 6, fontFamily: "monospace" }}>
                  {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                </div>
                <div style={{ fontSize: 11, color: "#78716C" }}>
                  {new Date(p.at).toLocaleTimeString("en-IN")}
                  {p.accuracy ? ` · ±${Math.round(p.accuracy)}m` : ""}
                </div>
                <a
                  href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: "inline-block",
                    marginTop: 8,
                    fontSize: 12,
                    color: "#B94A3E",
                  }}
                >
                  Open in Google Maps →
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
